import { z } from "zod";

export const clockPosition = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  accuracy: z.number().positive().max(100_000),
  observedAt: z.iso.datetime(),
}).strict();
export type ClockPosition = z.infer<typeof clockPosition>;

export const punchClockBody = z.object({
  requestId: z.uuid(),
  action: z.enum(["in", "out"]),
  locationId: z.uuid(),
  shiftId: z.uuid().optional(),
  position: clockPosition.nullable(),
  exceptionReason: z.string().trim().min(5).max(500).optional(),
}).strict().superRefine((body, ctx) => {
  if (body.action === "out" && !body.shiftId) ctx.addIssue({ code: "custom", path: ["shiftId"], message: "Choose the shift to end." });
  if (body.action === "in" && (body.shiftId || body.exceptionReason || !body.position)) ctx.addIssue({ code: "custom", message: "Punching in needs your current location." });
});

export const setClockSiteBody = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  radiusMeters: z.number().int().min(25).max(2000),
  enabled: z.boolean(),
  /**
   * The address the center was looked up from, kept beside the coordinates.
   * Omitted leaves the stored address alone; null clears it.
   */
  address: z.string().trim().min(1).max(300).nullable().optional(),
}).strict();

/**
 * `GET /time-clock/address-lookup?q=` — find a worksite's center from its
 * address (23 October 2026). Administrators only, like the boundary itself.
 */
export const addressLookupQuery = z.object({
  q: z.string().trim().min(3).max(300),
}).strict();
export type AddressLookupQuery = z.infer<typeof addressLookupQuery>;

/**
 * One place the lookup found. `exact` is false for a town, a postal code or a
 * road rather than a street address: its point is that area's center, too far
 * from any yard for a boundary of 25 to 2,000 meters, so the screen says so.
 */
export interface AddressMatch {
  label: string;
  latitude: number;
  longitude: number;
  exact: boolean;
  /** The lookup's own confidence in the match. */
  confidence: "high" | "medium" | "low";
}

/** `configured: false` means no Azure Maps key here; enter coordinates instead. */
export interface AddressLookupResponse {
  configured: boolean;
  matches: AddressMatch[];
}

/**
 * A worksite's shift length and zone — TRBUD-424. `shiftMinutes: null` turns
 * the bay alert off at that worksite, which is also where every worksite starts.
 */
export const setClockSiteShiftBody = z.object({
  shiftMinutes: z.number().int().min(60).max(1440).nullable(),
  timeZone: z.string().trim().min(1).max(64),
}).strict();
export type SetClockSiteShiftBody = z.infer<typeof setClockSiteShiftBody>;

/** A manager's later out time for one shift. Append-only; the punch stays. */
export const correctClockShiftBody = z.object({
  requestId: z.uuid(),
  correctedOutAt: z.iso.datetime(),
  reason: z.string().trim().min(5).max(500),
}).strict();
export type CorrectClockShiftBody = z.infer<typeof correctClockShiftBody>;

/** The terminal of one bay visit, named by an admin or manager. */
export const setBayTerminalBody = z.object({
  locationId: z.uuid(),
  reason: z.string().trim().max(500).optional(),
}).strict();
export type SetBayTerminalBody = z.infer<typeof setBayTerminalBody>;

export const requestClockExceptionBody = z.object({
  requestId: z.uuid(), locationId: z.uuid(), reason: z.string().trim().min(5).max(500),
}).strict();
export const decideClockExceptionBody = z.object({
  requestId: z.uuid(), decision: z.enum(["approve", "reject"]), reason: z.string().trim().min(5).max(500),
}).strict();
export const clockExceptionQuery = z.object({ status: z.enum(["pending", "approved", "rejected"]).default("pending"), id: z.uuid().optional() }).strict();

export interface ClockSite {
  locationId: string;
  name: string;
  latitude: number | null;
  longitude: number | null;
  radiusMeters: number;
  enabled: boolean;
  /** One shift here, in minutes; null means the bay alert is off (TRBUD-424). */
  shiftMinutes: number | null;
  timeZone: string;
  /** The address the center was looked up from; null when it was typed or taken from a phone. */
  address: string | null;
}
export interface ClockEvidence {
  status: "verified" | "exception" | "manager_approved_exception";
  distanceMeters: number | null;
  accuracy: number | null;
  latitude: number | null;
  longitude: number | null;
  observedAt: string | null;
  reason: string | null;
  siteLatitude: number;
  siteLongitude: number;
  radiusMeters: number;
}
export interface ClockShift {
  id: string;
  userId: string;
  userName: string;
  locationId: string;
  locationName: string;
  punchedInAt: string;
  punchedOutAt: string | null;
  inEvidence: ClockEvidence;
  outEvidence: ClockEvidence | null;
  /**
   * The latest manager correction of the out time, or null. The punch above is
   * never changed; `effectiveOutAt` is what attendance readers use.
   */
  correction: ClockShiftCorrection | null;
  /** The corrected out time when there is one, else the punched one. */
  effectiveOutAt: string | null;
}
export interface ClockShiftCorrection {
  id: string;
  correctedOutAt: string;
  reason: string;
  correctedByName: string;
  correctedAt: string;
}
export interface TimeClockResponse {
  sites: ClockSite[];
  activeShift: ClockShift | null;
  shifts: ClockShift[];
  canConfigure: boolean;
  canViewTeam: boolean;
  /** True when an administrator can look a worksite up by address here. */
  canLookUpAddress: boolean;
}

export interface TimeClockExceptionRequest {
  id: string;
  userId: string;
  userName: string;
  locationId: string;
  locationName: string;
  requestDay: string;
  timeZone: string;
  requestedAt: string;
  reason: string;
  status: "pending" | "approved" | "rejected";
  decidedAt: string | null;
  decidedByName: string | null;
  decisionReason: string | null;
  shiftId: string | null;
}

export interface TimeClockEligibility {
  fleetDay: string;
  timeZone: string;
  serverTime: string;
  nextFleetDayAt: string;
  isMechanic: boolean;
  requiresClockIn: boolean;
  hasClockedInToday: boolean;
  activeShift: ClockShift | null;
  exceptionRequest: TimeClockExceptionRequest | null;
}

/*
 * ════════════════════════════════════════════════════════════════════
 *  Clocked-in time in the bay — TRBUD-424
 * ════════════════════════════════════════════════════════════════════
 * When an in-house job's clocked-in time in the bay passes two shifts, Matt is
 * told once and the job is flagged. Nothing is blocked. The figure measures
 * attendance in the bay, not labour, which is why its label says so.
 *
 * Computed by `tb_bay_budget_state` in the database; see migration
 * 20261023185456_bay_budget_routines for the formula.
 */

/** The words every screen prints for the figure. */
export const BAY_BUDGET_LABEL = "Clocked-in time in bay";
/** The tag a job carries while its visit is over budget. Never red or orange. */
export const BAY_BUDGET_OVER_TAG = "Over 2 shifts";

/**
 * - `not_configured`: the terminal has no shift length yet, so the alert is off.
 * - `uncovered`: no terminal is known, or nobody assigned was clocked in at it
 *   while the truck was in. Never read as "under".
 * - `under`, `over`: against two shift lengths.
 * - `review`: under, but a punch longer than one shift (or still open past one)
 *   was capped at one shift, so a manager should check it.
 */
export const bayBudgetStates = ["not_configured", "uncovered", "under", "review", "over"] as const;
export type BayBudgetState = (typeof bayBudgetStates)[number];

export const bayTerminalBases = ["gps", "clock_in", "manual"] as const;
export type BayTerminalBasis = (typeof bayTerminalBases)[number];

export interface BayBudget {
  /** The visit this describes: the job's newest one. */
  episodeId: string;
  /** True while the truck is still in the bay on this visit. */
  open: boolean;
  startedAt: string;
  endedAt: string | null;
  state: BayBudgetState;
  /** Over two shifts on an open visit: the "Over 2 shifts" tag. Every viewer gets this. */
  over: boolean;
  /** A capped punch contributed, over or under. */
  needsReview: boolean;
  /**
   * The minutes, or null when withheld. Only management and the job's own
   * mechanics see the figures (Eden, 22 October 2026); everyone else gets the
   * tag alone. Null too where they cannot be computed (no terminal, no shift).
   */
  minutes: number | null;
  budgetMinutes: number | null;
  shiftMinutes: number | null;
  /** Time the truck has spent in the bay on this visit, punches aside. */
  bayMinutes: number | null;
  /** Part and approval waits, subtracted from the figure. */
  waitMinutes: number | null;
  /** Null while unknown; the job then reads "uncovered" until a manager sets it. */
  terminal: { locationId: string; name: string; basis: BayTerminalBasis } | null;
  /**
   * The alert, once recorded for this visit. `noLongerOver` when the live
   * figure has since come back under (a punch correction, a new terminal): the
   * record stays as history.
   */
  breach: { recordedAt: string; minutes: number | null; noLongerOver: boolean } | null;
}
